/**
 * Per-user reads for the Users → User Info screen. Everything here is fetched
 * for ONE user when their info screen opens — never for the whole list, which
 * is why none of it lives in the listing's snapshot.
 *
 * What exists to read (watchlock firestore.rules, verified 2026-10-28):
 *   - `watches` where userId == uid — admin list access, counts only. The watch
 *     payload is client-side ENCRYPTED, so brand/model are unreadable by design;
 *     there is no per-user brand count of owned watches to be had.
 *   - `users/{uid}/bookmarks` — admin-readable; `type` is brand / manufacturer /
 *     reference / calibre. This is where "favourite brands" lives.
 *   - `articles` where createdByUid == uid — authored articles (contributors).
 *   - `analyticsEvents` where uid == uid — the homegrown event log (Firebase
 *     Analytics is off), admin-only read, `createdAt` server timestamp.
 *
 * There is NO per-user error or failed-call store: function errors go to Cloud
 * Logging only. The screen links there (cloudLoggingUrl) instead of pretending.
 */
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firebase';

const PROJECT_ID = 'watchlock-1e53d';

export interface UserActivitySummary {
  /** Null when the count could not be read. */
  watches: number | null;
  /** Bookmark counts by `type`; null when unreadable. */
  bookmarksByType: Record<string, number> | null;
  /** Display names of bookmarked brands, alphabetical. */
  bookmarkedBrands: string[];
  articlesAuthored: number | null;
}

/** Group bookmark docs by `type`, and list the brand ones by name. */
export function summariseBookmarks(
  docs: ReadonlyArray<Record<string, unknown>>,
): { byType: Record<string, number>; brands: string[] } {
  const byType: Record<string, number> = {};
  const brands: string[] = [];
  for (const d of docs) {
    const type = typeof d.type === 'string' && d.type ? d.type : 'other';
    byType[type] = (byType[type] ?? 0) + 1;
    if (type === 'brand') {
      const name = typeof d.displayName === 'string' && d.displayName ? d.displayName : String(d.entityId ?? '');
      if (name) brands.push(name);
    }
  }
  return { byType, brands: brands.sort((a, b) => a.localeCompare(b)) };
}

export async function getUserActivitySummary(uid: string): Promise<UserActivitySummary> {
  const [watches, bookmarks, articles] = await Promise.allSettled([
    getCountFromServer(query(collection(db, 'watches'), where('userId', '==', uid))),
    getDocs(collection(db, 'users', uid, 'bookmarks')),
    getCountFromServer(query(collection(db, 'articles'), where('createdByUid', '==', uid))),
  ]);
  const summary =
    bookmarks.status === 'fulfilled' ? summariseBookmarks(bookmarks.value.docs.map((d) => d.data())) : null;
  return {
    watches: watches.status === 'fulfilled' ? watches.value.data().count : null,
    bookmarksByType: summary?.byType ?? null,
    bookmarkedBrands: summary?.brands ?? [],
    articlesAuthored: articles.status === 'fulfilled' ? articles.value.data().count : null,
  };
}

export interface AnalyticsEventRow {
  id: string;
  event: string;
  params: Record<string, unknown>;
  /** 'server' for webhook/function-written events; absent for the app's own. */
  source?: string;
  createdAt: Date | null;
}

export interface RecentEventsResult {
  events: AnalyticsEventRow[];
  /**
   * False when the `uid + createdAt desc` composite index is missing and the
   * list was built from an unordered sample instead — "recent" is then only
   * the newest of what was sampled.
   */
  ordered: boolean;
  /** The console link Firestore offers to create the missing index. */
  indexUrl?: string;
}

/** Without the index, how many of the user's events to sample and sort locally. */
const UNORDERED_SAMPLE = 500;

function toRow(id: string, data: Record<string, unknown>): AnalyticsEventRow {
  const ts = data.createdAt as { toDate?: () => Date } | undefined;
  return {
    id,
    event: typeof data.event === 'string' ? data.event : '(unnamed)',
    params: data.params && typeof data.params === 'object' ? (data.params as Record<string, unknown>) : {},
    source: typeof data.source === 'string' ? data.source : undefined,
    createdAt: typeof ts?.toDate === 'function' ? ts.toDate() : null,
  };
}

/** Newest first; undated rows last. */
export function sortEventsNewestFirst(rows: AnalyticsEventRow[]): AnalyticsEventRow[] {
  return [...rows].sort((a, b) => (b.createdAt?.getTime() ?? -Infinity) - (a.createdAt?.getTime() ?? -Infinity));
}

/** The index-creation link inside a Firestore FAILED_PRECONDITION message. */
export function extractIndexUrl(message: string): string | undefined {
  return message.match(/https:\/\/console\.firebase\.google\.com\/\S+/)?.[0];
}

export async function getRecentAnalyticsEvents(uid: string, max = 50): Promise<RecentEventsResult> {
  const events = collection(db, 'analyticsEvents');
  try {
    const snap = await getDocs(query(events, where('uid', '==', uid), orderBy('createdAt', 'desc'), limit(max)));
    return { events: snap.docs.map((d) => toRow(d.id, d.data())), ordered: true };
  } catch (err) {
    const code = (err as { code?: string }).code;
    if (code !== 'failed-precondition') throw err;
    const snap = await getDocs(query(events, where('uid', '==', uid), limit(UNORDERED_SAMPLE)));
    return {
      events: sortEventsNewestFirst(snap.docs.map((d) => toRow(d.id, d.data()))).slice(0, max),
      ordered: false,
      indexUrl: extractIndexUrl(err instanceof Error ? err.message : String(err)),
    };
  }
}

/** Cloud Logging, filtered to ERROR+ entries that mention this uid. */
export function cloudLoggingUrl(uid: string): string {
  const q = `severity>=ERROR\n"${uid}"`;
  return `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(q)};duration=P30D?project=${PROJECT_ID}`;
}
